import {
  BID_ADJUSTMENT_DECIMALS,
  BidderSeat,
  BidderSeatUpdatePayload,
  displayBidderSeatName,
  objectBidParamNames,
  PBSTCK_BIDDER_SEAT_CODE,
  PBSTCK_BIDDER_SEAT_NAME_REGEX,
  roundBidAdjustment,
} from '@pubstack/common/src/bidder-seat'
import { BidderCatalog, BidParamType, BidParamValue, isMarketplaceAlias, SeatBidParamSpec } from '@pubstack/common/src/bidderCatalog'
import { BidderParamValidation, BidderParamValidationType } from '@pubstack/common/src/bidderParamValidation'
import { useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'
import { FunctionComponent, useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { BID_PARAMS_MAPPINGS_QUERY_KEYS, useGetMappingFile, useGetTemplateFile, useMappingUpload } from '~/api/bid-params-mappings.api'
import { BIDDER_SEATS_QUERY_KEYS, useDeleteBidderSeat, useUpdateBidderSeat } from '~/api/bidder-seats.api'
import { SITES_QUERY_KEYS } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Title_P1 } from '~/components/_styled/titles'
import { Alert } from '~/components/Alert'
import Button from '~/components/Button'
import { CardTabs } from '~/components/CardTabs'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { Dropzone } from '~/components/Dropzone/Dropzone'
import { Tooltip } from '~/components/Tooltip'
import { Flyout } from '~/components/Flyout'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { isSeatMapped } from '../myBidderSeatTabs'
import { DeleteBidderSeatModal } from '../DeleteBidderSeatModal'
import { ConfirmFilteredMappingDownloadModal } from './ConfirmFilteredMappingDownloadModal'
import { ConfirmObjectBidParamsModal } from './ConfirmObjectBidParamsModal'
import { hasObjectBidParamsError, ObjectBidParamsDraft, ObjectBidParamsSection, removedKeys, retypedKeys, toExtraObjectBidParams, toKeyRenames } from './ObjectBidParamsSection'
import { canFilterByStackedAdUnits } from './stackedAdUnits'
import { useStackedAdUnitsOnly } from './useStackedAdUnitsOnly'
import { label, prefix, TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { RadioButtonGroup } from '~/components/RadioButtonGroup'
import { useToast } from '~/components/Toast/useToasts'
import { BidAdjustment } from '~/feature-components/BidAdjustment'
import { downloadCSVData, triggerBrowserDownload } from '~/utils/fileDownload'
import { Product } from '~/hooks/useProduct'
import { MAPPING_TYPE_LABEL } from '~/modules/pbs/integrations/common/seatSetupOption'
import { useScopePermissions } from '~/auth/useScopePermissions'

const MAX_ERRORS_DISPLAYED = 10

const sectionClassName = 'flex flex-col gap-2'
const infoClassName = 'text-sm leading-[1.375rem] text-hurricane italic'

const formatValidationError = (error: BidderParamValidation<BidderParamValidationType>): string => {
  switch (error.type) {
    case BidderParamValidationType.MISSING_PARAMS: {
      const detail = error.detail as { paramName: string }
      return `Missing required parameter "${detail.paramName}"`
    }
    case BidderParamValidationType.WRONG_TYPE: {
      const detail = error.detail as { paramName: string; expectedType: string }
      return `Wrong type for "${detail.paramName}" (expected ${detail.expectedType})`
    }
    case BidderParamValidationType.DUPLICATE_ENTRY: {
      const detail = error.detail as { siteName: string; adUnitName: string; device?: string; mediaType?: string }
      const parts = [detail.siteName, detail.adUnitName]
      if (detail.device) parts.push(detail.device)
      if (detail.mediaType) parts.push(detail.mediaType)
      return `Duplicate entry for ${parts.join(' - ')}`
    }
    default:
      return 'Unknown error'
  }
}

type GroupedError = {
  message: string
  lines: number[]
}

const groupErrorsByMessage = (errors: BidderParamValidation<BidderParamValidationType>[]): GroupedError[] => {
  const grouped = new Map<string, number[]>()

  for (const error of errors) {
    const message = formatValidationError(error)
    const lines = grouped.get(message) ?? []
    if (error.line !== undefined) {
      lines.push(error.line)
    }
    grouped.set(message, lines)
  }

  return Array.from(grouped.entries()).map(([message, lines]) => ({
    message,
    lines: lines.sort((a, b) => a - b),
  }))
}

// Constants
const TO_SPECIFIC_MAPPING_MESSAGE = "You are about to change your mapping settings. Your current mapping will be used by default on all compatible media types. This change can't be undone."
const TO_UNIFORM_MAPPING_MESSAGE =
  "You are about to change your mapping settings. The banner mapping will be used by default to your mapping. The mapping for other media types will be lost. This change can't be undone."

type FormModel = {
  bidAdjustment: {
    enable: boolean
    adjustment: number
  }
  specificPerMediaType: boolean
  /** Editable seat params, keyed by spec name. Held as strings because the inputs are text; coerced back on save. */
  seatBidParams: Record<string, string>
  /** Custom keys declared under each `object` bid param, keyed by that param's name. */
  extraObjectBidParams: ObjectBidParamsDraft
}

/**
 * Coerce a form string back to the type the spec declares, so the stack output keeps `member: 12070`,
 * not `"12070"`. `undefined` when the text is not that type: the field is free text, and `Number('12 070')`
 * is `NaN`, which `JSON.stringify` puts on the wire as `null` — a partner-mandated constant silently blanked.
 */
const coerceSeatBidParamValue = (raw: string, type: BidParamType): BidParamValue | undefined => {
  if (type === 'number') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  if (type === 'boolean') return raw === 'true'
  if (type === 'array') return raw.split(',').map((part) => part.trim())
  return raw
}

/** Single source of the form values, shared by `useForm`'s defaultValues and the `bidderSeat` reset so no key silently drops from the reset payload. */
export const buildFormModel = (bidderSeat: BidderSeat): FormModel => ({
  bidAdjustment: {
    enable: bidderSeat.bidAdjustment.enabled,
    // value is the fraction (≤4 decimals); ×100 reintroduces float noise, so round back to the 2-decimal percentage the input uses.
    adjustment: Number(((bidderSeat.bidAdjustment.value ?? 0) * 100).toFixed(BID_ADJUSTMENT_DECIMALS - 2)),
  },
  specificPerMediaType: bidderSeat.mappingDimensions.includes('mediaType'),
  seatBidParams: Object.fromEntries((bidderSeat.seatBidParams ?? []).map((spec) => [spec.name, spec.default === undefined ? '' : String(spec.default)])),
  extraObjectBidParams: Object.fromEntries(
    Object.entries(bidderSeat.extraObjectBidParams ?? {}).map(([namespace, specs]) => [
      namespace,
      // The `saved*` copies are what a later edit is compared against, to detect a rename or a type change.
      specs.map((spec) => ({ name: spec.name, type: spec.type, savedName: spec.name, savedType: spec.type })),
    ])
  ),
})

/** Editable specs take the form value; a cleared input means "leave as is" — coercing '' would fabricate 0 / false / ['']. */
export const mergeEditableSeatBidParams = (specs: SeatBidParamSpec[] | undefined, formValues: FormModel['seatBidParams'] | undefined): SeatBidParamSpec[] | undefined =>
  specs?.map((spec) => {
    const raw = formValues?.[spec.name]
    const coerced = spec.editable && raw !== undefined && raw.trim() !== '' ? coerceSeatBidParamValue(raw, spec.type) : undefined
    // Unparseable input keeps the stored default rather than overwriting it with nothing.
    return coerced === undefined ? spec : { ...spec, default: coerced }
  })

type Props = {
  bidderSeat: BidderSeat
  /** Explicit for the same reason as `BidderMapping`'s — see that component's `product` prop doc. */
  product: Product
  /** Optional. Without it the seat shows no custom-key section, since the namespaces come from the catalog entry. */
  catalogBidder?: BidderCatalog
  /** Which tab to open first. The `ext` column header opens this flyout directly on the seat settings. */
  initialTab?: 'seat-settings'
  onClose: () => void
}

export const MappingOptionsFlyout: FunctionComponent<Props> = ({ bidderSeat, product, catalogBidder, initialTab, onClose }) => {
  const user = useAuthenticatedUser()
  const { canEdit, disabledReason } = useScopePermissions()
  const queryClient = useQueryClient()
  const modal = useGlobalModal()
  const navigate = useNavigate()
  const toast = useToast()

  // Tracking labels
  const APPLY_MAPPING_OPTIONS_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-mapping-options-save`
  const SET_PER_MEDIA_TYPE_TRACKING_LABEL: `${prefix}-${label}` = `${product}-bidder-seat-mapping-options-per-media-type`
  const TOGGLE_BID_ADJUSTMENT_TRACKING_LABEL: `${prefix}-${label}` = `${product}-bidder-seat-settings-bid-adjustment`
  const DOWNLOAD_FILTERED_MAPPING_TRACKING_LABEL: `${prefix}-${label}` = `${product}-bidder-seat-mapping-download-filtered`
  const DELETE_SEAT_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-delete`

  // API Hooks
  const { mutateAsync: updateBidderSeat, isPending: isBidderSeatUpdatePending } = useUpdateBidderSeat(product, bidderSeat.id, {
    successToast: (
      <>
        Advanced options saved for <strong>{bidderSeat?.name}</strong>
      </>
    ),
    errorToast: 'Error while saving the mapping options',
  })
  const { mutateAsync: deleteBidderSeat } = useDeleteBidderSeat(product, bidderSeat.id, {
    successToast: (
      <>
        <strong>{displayBidderSeatName(bidderSeat)}</strong> was deleted.
      </>
    ),
    // Names the cause rather than just the failure. A seat that got past the check on click can only be
    // refused for one reason — something is still mapped on it — and for a Spark For Pub seat that
    // something can be the Spark team's half of the pair, which this screen has no counter for.
    errorToast: 'Could not delete the seat. It may still have mapped ad units.',
  })
  const { fetch: downloadTemplateFile, isLoading: isTemplateFileDownloading } = useGetTemplateFile(product, bidderSeat.id)
  const { fetch: downloadMappingFile, isLoading: isMappingFileDownloading } = useGetMappingFile(product, bidderSeat.id)
  // A second instance rather than a parameter on `fetch`: `useLazyQuery` builds its URL and its key when
  // the hook runs, so the filtered file needs its own.
  const { fetch: downloadStackedMappingFile, isLoading: isStackedMappingFileDownloading } = useGetMappingFile(product, bidderSeat.id, undefined, true)
  // Read-only: the switch that sets it is on the seat header, above the spreadsheet it also narrows.
  const [stackedAdUnitsOnly] = useStackedAdUnitsOnly()
  // The same answer the mapping view uses to decide whether to render the filter switch at all.
  const isStackedFilterOffered = canFilterByStackedAdUnits(bidderSeat.mappingDimensions)

  // Upload state machine
  const {
    uploadState,
    submitUpload,
    isLoading: isUploadLoading,
    currentFile,
  } = useMappingUpload({
    product,
    bidderSeatId: bidderSeat.id,
    scopeId: user.currentScopeId,
    onSuccess: () => {
      // Mappings queries are keyed [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, ...]; a CSV import can touch many sites, so invalidate the whole product prefix
      queryClient.invalidateQueries({ queryKey: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS] })
      queryClient.invalidateQueries({ queryKey: SITES_QUERY_KEYS(bidderSeat.scopeId) })
      // Bidder-seat queries are keyed [product, ...BIDDER_SEATS_QUERY_KEYS, ...]; without the product prefix the seat (and its mapping status) would never refetch
      queryClient.invalidateQueries({ queryKey: [product, ...BIDDER_SEATS_QUERY_KEYS] })
      toast.success(
        <>
          Mapping uploaded for <strong>{bidderSeat.name}</strong>
        </>
      )
    },
  })

  // Validation errors - only show when in error state
  const validationErrors = uploadState.status === 'error' ? uploadState.errors : []
  const errorMessage = uploadState.status === 'error' ? uploadState.message : undefined
  const groupedErrors = groupErrorsByMessage(validationErrors)
  const displayedErrors = groupedErrors.slice(0, MAX_ERRORS_DISPLAYED)
  const remainingErrorsCount = groupedErrors.length - MAX_ERRORS_DISPLAYED

  // Check if this is a Spark bidder (name ends with '-spark')
  const isSparkBidder = useMemo(() => PBSTCK_BIDDER_SEAT_NAME_REGEX.test(bidderSeat.name), [bidderSeat.name])
  // sparkForPub seats expose the Files (CSV isEnabled) and Seat settings tabs, but not the per-media-type
  // Mapping tab: it is managed by the Spark team and would break the forced site/adUnit dimensions.
  const isSparkForPub = bidderSeat.type === 'spark_for_pubs'
  const isMarketplace = isMarketplaceAlias(bidderSeat?.name ?? '') && !user.isAdmin
  // The internal seats and the customer's Spark seat are provisioned and torn down by onboarding, and the
  // API refuses to delete them; a Marketplace seat is Pubstack's to manage. The same three this flyout
  // already treats as not the customer's, so the danger zone is simply not rendered for them.
  const isSeatDeletable = !bidderSeat.isInternal && bidderSeat.bidderCode !== PBSTCK_BIDDER_SEAT_CODE && !isMarketplace

  const formMethods = useForm<FormModel>({
    defaultValues: buildFormModel(bidderSeat),
    mode: 'onChange',
  })

  /**
   * Seat-level bid params, at the top of Seat settings.
   *
   * Pubstack-operated: they carry integration constants a partner mandates (gingerad's `member`,
   * `use_pmt_rule`), so they stay staff-only and the enforced ones are read-only — the server re-reads
   * those from the catalog on save regardless. Staff-only is the reason they had a tab of their own; it
   * is not a reason for a second place to look, so the section simply is not rendered for anyone else.
   */
  const seatBidParamsSection =
    user.isAdmin && bidderSeat.seatBidParams?.length ? (
      <div className={sectionClassName}>
        <Title_P1>Seat-level bid params</Title_P1>
        <div className={infoClassName}>Applied to every mapping of this seat. Not shown to publishers and never part of the mapping CSV.</div>
        {bidderSeat.seatBidParams.map((spec) =>
          spec.editable ? (
            <Input key={spec.name} label={spec.name} control={formMethods.control} name={`seatBidParams.${spec.name}`} />
          ) : (
            <div key={spec.name} className="flex justify-between gap-4 text-sm leading-[1.375rem] text-hurricane">
              <span>{spec.name}</span>
              <strong>{String(spec.default ?? '')}</strong>
            </div>
          )
        )}
      </div>
    ) : null

  /**
   * Custom keys under the bidder's `object` params, in Seat settings next to the rest.
   *
   * Unlike the staff-only section above, this one is for publishers: the keys are theirs, and the values
   * go in their mapping. It is hidden when the bidder declares no `object` param.
   */
  const objectNamespaces = catalogBidder ? objectBidParamNames(catalogBidder.bidParams) : []
  const objectBidParamsDraft = useWatch({ control: formMethods.control, name: 'extraObjectBidParams' })
  // Without this, a half-typed key is dropped silently on save, and its column goes with it.
  const hasInvalidObjectBidParams = hasObjectBidParamsError(objectBidParamsDraft ?? {})
  const objectBidParamsSection = objectNamespaces.length ? (
    // Separated from the section above: the cards are their own block, and putting them right after the
    // bid adjustment reads as one long form.
    <div className={`mt-6 ${sectionClassName}`}>
      <Title_P1>Custom Seat Parameters</Title_P1>
      {/* Said once for the whole section: every namespace below works the same way. */}
      <p className="mb-2 text-sm text-[var(--mantine-color-gray-7)]">Each key you declare becomes a column in the mapping, where you fill its value per ad unit.</p>
      <ObjectBidParamsSection
        namespaces={objectNamespaces}
        value={objectBidParamsDraft ?? {}}
        disabled={!canEdit}
        onChange={(value) => formMethods.setValue('extraObjectBidParams', value, { shouldDirty: true })}
      />
    </div>
  ) : null

  /** Answered here rather than on the button: what stops a delete is told in the dialog, not in a tooltip. */
  const handleDeleteClick = () => {
    modal.open(DeleteBidderSeatModal, {
      seatName: displayBidderSeatName(bidderSeat),
      isMapped: isSeatMapped(bidderSeat),
      trackingPrefix: DELETE_SEAT_TRACKING_LABEL,
      onDelete: async () => {
        await deleteBidderSeat()
        // Innermost first: the seat is gone, and neither the dialog nor the flyout behind it has anything
        // left to render for it.
        modal.close()
        onClose()
        navigate('/monetization/inventory/my-bidder-seats')
      },
    })
  }

  /**
   * Deleting the seat, at the bottom of Seat settings.
   *
   * Only shown for a seat the publisher owns: the internal seats and the customer's Spark seat are
   * provisioned and torn down by onboarding and the API refuses to delete them, and a Marketplace seat is
   * Pubstack's to manage — the same three the rest of this flyout already treats as not the customer's.
   *
   * The button is never disabled on mapping status, only on the role. What blocks a delete is checked when
   * it is clicked, and said in the dialog — see `DeleteBidderSeatModal`. It sits outside the form, so
   * it does not wait on Apply.
   */
  const dangerZoneSection = isSeatDeletable ? (
    <div className={sectionClassName}>
      <Title_P1>Delete this seat</Title_P1>
      <div className={infoClassName}>The bidder stops being called and its mapping is deleted with it. A seat cannot be archived, and this cannot be undone.</div>
      <Tooltip title={disabledReason} disabled={canEdit} reposition>
        <div className={'self-start'}>
          <Button variant={'negative'} iconName={'delete'} disabled={!canEdit} onClick={handleDeleteClick} trackingLabel={DELETE_SEAT_TRACKING_LABEL}>
            Delete seat
          </Button>
        </div>
      </Tooltip>
    </div>
  ) : null

  const performSave = async (formModel: FormModel) => {
    const mappingDimensions = [...(bidderSeat.mappingDimensions || [])]

    // Handle media type dimension
    if (formModel.specificPerMediaType && !mappingDimensions.includes('mediaType')) {
      mappingDimensions.push('mediaType')
    } else if (!formModel.specificPerMediaType && mappingDimensions.includes('mediaType')) {
      const index = mappingDimensions.indexOf('mediaType')
      mappingDimensions.splice(index, 1)
    }

    const toUpdateBidderSeat: BidderSeatUpdatePayload = {
      ...bidderSeat,
      bidAdjustment: {
        enabled: formModel.bidAdjustment.enable,
        value: roundBidAdjustment(formModel.bidAdjustment.adjustment / 100),
      },
      mappingDimensions,
      // Only editable specs are sent back; the server re-reads every enforced value from the catalog.
      seatBidParams: mergeEditableSeatBidParams(bidderSeat.seatBidParams, formModel.seatBidParams),
      extraObjectBidParams: toExtraObjectBidParams(formModel.extraObjectBidParams),
      // Read by the update and never stored: it tells the server which keys moved, so their values move too.
      keyRenames: toKeyRenames(formModel.extraObjectBidParams),
    }

    if (!isEqual(bidderSeat, toUpdateBidderSeat)) {
      await updateBidderSeat(toUpdateBidderSeat)
      queryClient.invalidateQueries({ queryKey: [product, ...BID_PARAMS_MAPPINGS_QUERY_KEYS, bidderSeat.id] })
      queryClient.invalidateQueries({ queryKey: [...SITES_QUERY_KEYS(bidderSeat.scopeId), 'infinite'] })
    }

    onClose()
  }

  const onSubmit = async (value: FormModel) => {
    if (!bidderSeat || !formMethods.formState.isDirty || isBidderSeatUpdatePending) return

    const isMediaTypeChanging =
      (bidderSeat.mappingDimensions?.includes('mediaType') && !value.specificPerMediaType) || (!bidderSeat.mappingDimensions?.includes('mediaType') && value.specificPerMediaType)

    if (isMediaTypeChanging) {
      const message = value.specificPerMediaType ? TO_SPECIFIC_MAPPING_MESSAGE : TO_UNIFORM_MAPPING_MESSAGE

      modal.open(ConfirmationModal, {
        title: 'Settings confirmation',
        message,
        trackingLabel: APPLY_MAPPING_OPTIONS_TRACKING_LABEL,
        onCancel: () => modal.close(),
        onConfirm: async () => {
          modal.close()
          await performSave(value)
        },
      })
      return
    }

    // Both change a mapping column's name or remove it, so neither is done silently. One modal for both:
    // showing two in a row would make the second look like the first had failed.
    const toRemove = removedKeys(buildFormModel(bidderSeat).extraObjectBidParams, value.extraObjectBidParams)
    const toRename = toKeyRenames(value.extraObjectBidParams)
    const toReset = retypedKeys(value.extraObjectBidParams)
    if (toRemove.length > 0 || toRename.length > 0 || toReset.length > 0) {
      modal.open(ConfirmObjectBidParamsModal, {
        removed: toRemove,
        renamed: toRename,
        reset: toReset,
        trackingLabel: APPLY_MAPPING_OPTIONS_TRACKING_LABEL,
        onCancel: () => modal.close(),
        onConfirm: async () => {
          modal.close()
          await performSave(value)
        },
      })
      return
    }

    await performSave(value)
  }

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        await submitUpload(file)
      } catch {
        // Error already handled by the hook
      }
    },
    [submitUpload]
  )

  const handleDownload = useCallback(
    async (options?: { fullMapping?: boolean; onlyStackedAdUnits?: boolean }) => {
      const result = options?.fullMapping ? await (options.onlyStackedAdUnits ? downloadStackedMappingFile() : downloadMappingFile()) : await downloadTemplateFile()
      if (!result.data) return

      const fallbackFilename = options?.fullMapping ? `${bidderSeat.name}-mapping.csv` : `${bidderSeat.name}-template.csv`

      if (typeof result.data === 'object' && 'url' in result.data) {
        triggerBrowserDownload(result.data.url, result.data.filename || fallbackFilename)
      } else {
        downloadCSVData(result.data.toString(), fallbackFilename)
      }
    },
    [downloadTemplateFile, downloadMappingFile, downloadStackedMappingFile, bidderSeat.name]
  )

  /**
   * The mapping file, warning first when the spreadsheet is filtered.
   *
   * The filter is set on the mapping view and read here, two screens that never speak — so the warning
   * is where the user finds out the file will not match their inventory, and it offers the full file
   * beside the filtered one rather than making them go back and turn the filter off.
   */
  const handleMappingDownload = useCallback(() => {
    if (!stackedAdUnitsOnly || !isStackedFilterOffered) {
      handleDownload({ fullMapping: true })
      return
    }

    modal.open(ConfirmFilteredMappingDownloadModal, {
      trackingLabel: DOWNLOAD_FILTERED_MAPPING_TRACKING_LABEL,
      onCancel: () => modal.close(),
      onDownloadFiltered: () => {
        modal.close()
        handleDownload({ fullMapping: true, onlyStackedAdUnits: true })
      },
      onDownloadAll: () => {
        modal.close()
        handleDownload({ fullMapping: true })
      },
    })
  }, [stackedAdUnitsOnly, isStackedFilterOffered, handleDownload, modal, DOWNLOAD_FILTERED_MAPPING_TRACKING_LABEL])

  useEffect(() => {
    if (bidderSeat) {
      formMethods.reset(buildFormModel(bidderSeat))
    }
  }, [bidderSeat])

  return (
    <Flyout.Content>
      <Flyout.Title>Advanced options for all medias</Flyout.Title>
      <FormProvider {...formMethods}>
        <Flyout.Body>
          <CardTabs
            // Seat settings is the second tab when the bulk-upload tab is there, and the first when it is not.
            initialIndex={initialTab === 'seat-settings' && (!isSparkBidder || isSparkForPub) ? 1 : 0}
            tabs={[
              // sparkForPub always exposes the Files (CSV isEnabled) tab, even if its name matches the
              // internal `-spark` pattern that otherwise hides it.
              ...(!isSparkBidder || isSparkForPub
                ? [
                    {
                      label: 'Bulk Upload',
                      content: (
                        <div className={sectionClassName}>
                          <Title_P1>Download mapping</Title_P1>
                          <p>Retrieve your mapping into one csv for all your sites or download csv template.</p>
                          <div className="mb-4 flex gap-2">
                            <Button
                              variant="primary"
                              iconName={isMappingFileDownloading || isStackedMappingFileDownloading ? 'pubstack' : 'download'}
                              iconSpin={isMappingFileDownloading || isStackedMappingFileDownloading}
                              onClick={handleMappingDownload}
                              trackingLabel="pbs-bidder-seat-mapping-download"
                            >
                              Download current mapping
                            </Button>
                            <Button
                              variant="secondary"
                              iconName={isTemplateFileDownloading ? 'pubstack' : 'download'}
                              iconSpin={isTemplateFileDownloading}
                              onClick={() => handleDownload()}
                              trackingLabel="pbs-bidder-seat-template-download"
                            >
                              Download template
                            </Button>
                          </div>

                          <Title_P1>Upload new mapping</Title_P1>
                          <p>Transfer your mapping into Pubstack for all your sites.</p>
                          <Tooltip title={disabledReason} disabled={canEdit} reposition>
                            <Dropzone onDrop={handleUpload} currentFile={currentFile} loading={isUploadLoading} error={uploadState.status === 'error'} disabled={!canEdit} />
                          </Tooltip>

                          {groupedErrors.length > 0 && (
                            <Alert variant="danger" iconName="warning">
                              <div>
                                <strong>Validation errors found in your CSV:</strong>
                                <ul className="m-0 pl-5">
                                  {displayedErrors.map((error, index) => (
                                    <li key={index}>
                                      {error.lines.length > 0 && <strong>{error.lines.length === 1 ? `Line ${error.lines[0]}` : `Lines ${error.lines.join(', ')}`}: </strong>}
                                      {error.message}
                                    </li>
                                  ))}
                                </ul>
                                {remainingErrorsCount > 0 && (
                                  <p>
                                    ... and {remainingErrorsCount} more error{remainingErrorsCount > 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            </Alert>
                          )}

                          {uploadState.status === 'error' && !validationErrors.length && errorMessage && (
                            <Alert variant="danger" iconName="warning">
                              {errorMessage}
                            </Alert>
                          )}
                        </div>
                      ),
                    },
                  ]
                : []),
              {
                // Everything that is a setting of the seat itself, rather than of what it is mapped to.
                // "Bid" named only half of what belonged here, and the seat's own bid params sat in a tab
                // of their own two along — same subject, two places to look.
                label: 'Seat settings',
                content: (
                  <div className={sectionClassName}>
                    {/* Params first: they say what this seat *is* to the partner, and the adjustment is a
                        modifier applied on top of that. */}
                    {seatBidParamsSection}
                    <BidAdjustment trackingLabel={TOGGLE_BID_ADJUSTMENT_TRACKING_LABEL} disabled={isMarketplace}></BidAdjustment>
                    {objectBidParamsSection}
                    {dangerZoneSection}
                  </div>
                ),
              },
              ...(!isSparkForPub
                ? [
                    {
                      label: 'Mapping',
                      content: (
                        <div className={sectionClassName}>
                          <Title_P1>Customization settings</Title_P1>
                          <div className={infoClassName}>Only banner mapping is kept when switching from specific to uniform mapping.</div>
                          <RadioButtonGroup
                            options={[
                              /* Named exactly as the add-a-seat flow names them. The same two choices were
                                 phrased differently here, so a reader met their own earlier decision under
                                 wording they had never seen. */
                              {
                                label: MAPPING_TYPE_LABEL['ad-unit'],
                                value: false,
                                trackingLabel: `${SET_PER_MEDIA_TYPE_TRACKING_LABEL}-unset`,
                                disabled: isMarketplace || !canEdit,
                              },
                              {
                                label: MAPPING_TYPE_LABEL['media-type'],
                                value: true,
                                trackingLabel: `${SET_PER_MEDIA_TYPE_TRACKING_LABEL}-set`,
                                disabled: isMarketplace || !canEdit,
                              },
                            ]}
                            name="specificPerMediaType"
                            control={formMethods.control}
                          />
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Flyout.Body>
        <Flyout.Actions>
          <Button
            iconName={isBidderSeatUpdatePending ? 'pubstack' : undefined}
            iconSpin={isBidderSeatUpdatePending}
            disabled={!formMethods.formState.isDirty || isBidderSeatUpdatePending || !canEdit || hasInvalidObjectBidParams}
            onClick={formMethods.handleSubmit(onSubmit)}
            trackingLabel={APPLY_MAPPING_OPTIONS_TRACKING_LABEL}
          >
            {isBidderSeatUpdatePending ? 'Applying...' : 'Apply'}
          </Button>
        </Flyout.Actions>
      </FormProvider>
    </Flyout.Content>
  )
}
